import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'

import {
  ApiInventorySavedSearch,
  ApiInventorySavedSearchEditPayload,
  ApiInventorySavedSearchPayload,
  ApiPriceField,
  InventoryProduct,
} from '@/types'

type ProductsCache = Record<string, { objectID: string; itemNetPrice: ApiPriceField }>
type State = {
  productPricesCache: ProductsCache
  allSelectedProductsForAddingToCart: InventoryProduct[]
  inventorySavedSearches: ApiInventorySavedSearch[]
  isInventorySavedSearchesLoaded: boolean
}
type Getters = {
  uniqueSelectedProductsForAddingToCart: (state: State) => InventoryProduct[]
}
type Actions = {
  pushProductPricesToCache(
    productsPriceData: {
      objectID: string
      itemNetPrice: ApiPriceField
    }[],
  ): void
  getPriceForProducts(
    inventoryProducts: {
      objectID: string
      code: string
      salesToStock: number
      aliqPerLot: number
      volPerAliq: number
      uom: string
    }[],
  ): Promise<
    Array<
      | {
          objectID: string
          code: string
          salesToStock: number
          aliqPerLot: number
          volPerAliq: number
          uom: string
        }
      | {
          objectID: string
        }
    >
  >
  addProductToSelectionList(product: InventoryProduct): void
  removeProductFromSelectionList(product: InventoryProduct): void
  setSelectionList(products: InventoryProduct[]): void
  setInventorySavedSearches(): Promise<void>
  addInventorySavedSearch(inventorySavedSearch: ApiInventorySavedSearchPayload): Promise<void>
  deleteInventorySavedSearch(code: string): Promise<void>
  updateInventorySavedSearch(
    inventorySavedSearch: ApiInventorySavedSearchEditPayload,
  ): Promise<void>
  resetState(): void
}

export const useInventoryStore = defineStore<string, State, Getters, Actions>('inventory', {
  state: () => ({
    productPricesCache: {},
    allSelectedProductsForAddingToCart: [],
    inventorySavedSearches: [],
    isInventorySavedSearchesLoaded: false,
  }),

  getters: {
    uniqueSelectedProductsForAddingToCart: (state) => {
      const uniqueProductsMap = state.allSelectedProductsForAddingToCart.reduce<
        Record<string, InventoryProduct>
      >((acc, product) => {
        if (!acc[product.objectID]) {
          acc[product.objectID] = product
        }
        return acc
      }, {})
      return Object.values(uniqueProductsMap)
    },
  },

  actions: {
    pushProductPricesToCache(
      productsPriceData: {
        objectID: string
        itemNetPrice: ApiPriceField
      }[],
    ) {
      const productPrices = productsPriceData.reduce<ProductsCache>(
        (sum, { objectID, itemNetPrice }) => {
          sum[objectID] = {
            objectID,
            itemNetPrice,
          }
          return sum
        },
        {},
      )
      this.productPricesCache = { ...this.productPricesCache, ...productPrices }
    },

    async getPriceForProducts(
      inventoryProducts: {
        objectID: string
        code: string
        salesToStock: number
        aliqPerLot: number
        volPerAliq: number
        uom: string
      }[],
    ) {
      try {
        const productsToLoad = inventoryProducts.filter(
          ({ objectID }) => !this.productPricesCache[objectID],
        )
        if (productsToLoad.length) {
          const productsToLoadPayloads = productsToLoad.map(({ code, objectID, uom }) => ({
            productCode: code,
            priceParametersData: {
              inventoryId: objectID,
              salesUnit: uom,
              salesToStock: 1,
              aliqPerLot: 0,
              volPerAliq: 0,
            },
          }))
          const allPriceEntries = []

          const amountToLoadPerRequest = 20 // Prevent endpoint performance issues
          for (let i = 0; i < productsToLoadPayloads.length; i += amountToLoadPerRequest) {
            const chunk = productsToLoadPayloads.slice(i, i + amountToLoadPerRequest)
            const { data: priceEntries } = await api.products.getProductsPrices(chunk)
            allPriceEntries.push(...priceEntries)
          }

          const pricesToStoreInCache = allPriceEntries.map((priceEntry, idx) => {
            return {
              objectID: productsToLoad[idx].objectID,
              itemNetPrice: priceEntry.itemNetPrice,
            }
          })
          await this.pushProductPricesToCache(pricesToStoreInCache)
        }

        return inventoryProducts.map(({ objectID }) => {
          const priceData = this.productPricesCache[objectID]
          return priceData || { objectID }
        })
      } catch {
        return []
      }
    },

    addProductToSelectionList(product) {
      if (product.isAddedToCart) return
      const isAlreadyContains = this.allSelectedProductsForAddingToCart.some(
        (el) => el.uiId && product.uiId && el.uiId === product.uiId,
      )
      if (isAlreadyContains) return

      this.allSelectedProductsForAddingToCart = [
        ...this.allSelectedProductsForAddingToCart,
        product,
      ]
    },

    removeProductFromSelectionList(product) {
      const isContainsExactProduct = this.allSelectedProductsForAddingToCart.some(
        (el) => el.uiId === product.uiId,
      )
      if (isContainsExactProduct) {
        const withoutProvidedProduct = this.allSelectedProductsForAddingToCart.filter(
          (el) => el.uiId !== product.uiId,
        )
        this.allSelectedProductsForAddingToCart = withoutProvidedProduct
      }
    },

    setSelectionList(products) {
      this.allSelectedProductsForAddingToCart = [...products]
    },

    async setInventorySavedSearches() {
      const { data } = await api.user.getInventorySavedSearches()
      this.inventorySavedSearches = data
      this.isInventorySavedSearchesLoaded = true
    },

    async addInventorySavedSearch(inventorySavedSearch) {
      await api.user.addInventorySavedSearch(inventorySavedSearch)
      await this.setInventorySavedSearches()
    },

    async deleteInventorySavedSearch(code) {
      await api.user.deleteInventorySavedSearches(code)
      await this.setInventorySavedSearches()
    },

    async updateInventorySavedSearch(inventorySavedSearch) {
      await api.user.updateInventorySavedSearch(inventorySavedSearch)
      await this.setInventorySavedSearches()
    },

    resetState() {
      this.$reset()
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInventoryStore, import.meta.hot))
}
